/**
 * Order Card Component
 * Displays order details with actions for admin
 * Requirements: 16.2, 16.3
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  User,
  Phone,
  Calendar,
  MessageSquare,
  Wrench,
  Video,
  Settings,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  Star,
  Banknote,
} from 'lucide-react';
import type { Order, OrderBid, DisputeReason } from '@/lib/api';
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  ORDER_TYPE_LABELS,
} from '@/hooks/useOrders';

const DISPUTE_REASON_LABELS: Record<DisputeReason, string> = {
  POOR_QUALITY: 'Низкое качество',
  OVERCHARGED: 'Завышена цена',
  DAMAGE_CAUSED: 'Нанесён ущерб',
  MASTER_NO_SHOW: 'Мастер не пришёл',
  CLIENT_NO_SHOW: 'Клиент не пришёл',
  PAYMENT_ISSUE: 'Проблема с оплатой',
  SAFETY_CONCERN: 'Безопасность',
  OTHER: 'Другое',
};

interface OrderCardProps {
  order: Order;
  onAssignMaster?: (orderId: string, masterId: string) => void;
  onDispute?: (orderId: string, reason: DisputeReason, comment?: string) => void;
  onResolveDispute?: (orderId: string) => void;
  isAssigning?: boolean;
  isDisputing?: boolean;
}

const ORDER_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  VIDEO_CONSULT: Video,
  REPAIR: Wrench,
  MAINTENANCE: Settings,
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPrice(price: string | null): string {
  if (!price) return '—';
  const num = parseFloat(price);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(num);
}

export function OrderCard({
  order,
  onAssignMaster,
  onDispute,
  onResolveDispute,
  isAssigning,
  isDisputing,
}: OrderCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [disputeReason, setDisputeReason] = useState<DisputeReason | ''>('');
  const [disputeComment, setDisputeComment] = useState('');
  const [selectedBidId, setSelectedBidId] = useState<string | null>(null);

  const TypeIcon = ORDER_TYPE_ICONS[order.type] || Wrench;
  const statusColor = ORDER_STATUS_COLORS[order.status] || 'bg-gray-500/10 text-gray-500';
  const statusLabel = ORDER_STATUS_LABELS[order.status] || order.status;
  const typeLabel = ORDER_TYPE_LABELS[order.type] || order.type;

  const handleAssignMaster = (bid: OrderBid) => {
    if (onAssignMaster) {
      setSelectedBidId(bid.id);
      onAssignMaster(order.id, bid.master.id);
    }
  };

  const handleDispute = () => {
    if (onDispute && disputeReason) {
      onDispute(order.id, disputeReason, disputeComment.trim() || undefined);
      setShowDisputeDialog(false);
      setDisputeReason('');
      setDisputeComment('');
    }
  };

  const canAssign = order.status === 'BIDDING' && order.bids.length > 0 && !order.master;
  const canDispute = !['DISPUTED', 'CANCELED', 'REFUNDED'].includes(order.status);

  return (
    <>
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                <TypeIcon className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-sm">{typeLabel}</p>
                <p className="text-xs text-muted-foreground">#{order.id.slice(0, 8)}</p>
              </div>
            </div>
            <Badge className={statusColor}>{statusLabel}</Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Description */}
          <p className="text-sm line-clamp-2">{order.description || 'Без описания'}</p>

          {/* Client Info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <User className="h-3.5 w-3.5" />
              <span>{order.client.name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Phone className="h-3.5 w-3.5" />
              <span>{order.client.phone || 'Не указан'}</span>
            </div>
          </div>

          {/* Date & Price */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>{formatDate(order.createdAt)}</span>
            </div>
            {order.finalPrice && (
              <div className="flex items-center gap-1 font-medium">
                <Banknote className="h-3.5 w-3.5" />
                <span>{formatPrice(order.finalPrice)}</span>
              </div>
            )}
          </div>

          {/* Master Info (if assigned) */}
          {order.master && (
            <div className="rounded-md bg-muted/50 p-2">
              <p className="text-xs text-muted-foreground mb-1">Мастер</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="text-sm font-medium">{order.master.name}</span>
                </div>
                <div className="flex items-center gap-1 text-sm">
                  <Star className="h-3.5 w-3.5 text-yellow-500" />
                  <span>{order.master.rating.toFixed(1)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Bids Count */}
          {order.bids.length > 0 && !order.master && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MessageSquare className="h-3.5 w-3.5" />
              <span>Откликов: {order.bids.length}</span>
            </div>
          )}

          {/* Expand/Collapse Button */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Свернуть
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Подробнее
              </>
            )}
          </Button>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="space-y-3 pt-2 border-t">
              {/* Bids List */}
              {order.bids.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Отклики ({order.bids.length})</p>
                  {order.bids.map((bid) => (
                    <div
                      key={bid.id}
                      className="rounded-md border p-2 space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{bid.master.name}</span>
                          <div className="flex items-center gap-0.5 text-xs">
                            <Star className="h-3 w-3 text-yellow-500" />
                            <span>{bid.master.rating.toFixed(1)}</span>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-green-600">
                          {formatPrice(bid.price)}
                        </span>
                      </div>
                      {bid.comment && (
                        <p className="text-xs text-muted-foreground">{bid.comment}</p>
                      )}
                      {canAssign && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full mt-1"
                          onClick={() => handleAssignMaster(bid)}
                          disabled={isAssigning}
                        >
                          {isAssigning && selectedBidId === bid.id ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Назначение...
                            </>
                          ) : (
                            'Назначить'
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              {order.status === 'DISPUTED' && onResolveDispute ? (
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => onResolveDispute(order.id)}
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Разобрать спор
                </Button>
              ) : canDispute ? (
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => setShowDisputeDialog(true)}
                  disabled={isDisputing}
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Открыть спор
                </Button>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Открыть спор</DialogTitle>
            <DialogDescription>
              Укажите причину открытия спора по заказу #{order.id.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(DISPUTE_REASON_LABELS) as DisputeReason[]).map((reason) => (
              <Button
                key={reason}
                variant={disputeReason === reason ? 'default' : 'outline'}
                size="sm"
                className="text-xs"
                onClick={() => setDisputeReason(reason)}
              >
                {DISPUTE_REASON_LABELS[reason]}
              </Button>
            ))}
          </div>
          <Textarea
            placeholder="Комментарий (необязательно)..."
            value={disputeComment}
            onChange={(e) => setDisputeComment(e.target.value)}
            rows={2}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDisputeDialog(false)}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={handleDispute}
              disabled={!disputeReason || isDisputing}
            >
              {isDisputing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Открытие...
                </>
              ) : (
                'Открыть спор'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
